import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { FiInfo, FiX, FiMove } from "react-icons/fi";
import { cn } from "utils/helpers";
import type { ScoringInstruction, ScoringInstructionRow } from "../../../types/scoring.type";

interface ScoringInstructionsPanelProps {
	activeScore: number | null;
	anchorElement: HTMLElement | null;
	containerElement: HTMLElement | null;
	instructions: ScoringInstruction | null;
	isLoading?: boolean;
	isOpen: boolean;
	onToggle: () => void;
}

const PANEL_HEIGHT_ESTIMATE = 220;
const PANEL_MAX_WIDTH = 900;
const GAP = 8;
const VIEWPORT_PADDING = 16;

const formatScore = (score: number): string => {
	return Math.round(score).toString();
};

const RangeDisplay: React.FC<{ row: ScoringInstructionRow }> = ({ row }) => {
	const min = Math.round(row.minScore);
	const max = Math.round(row.maxScore);
	if (min === max) {
		return <>{min}</>;
	}
	return (
		<>
			{min}<span className="text-[0.65em] opacity-60 mx-px">&ndash;</span>{max}
		</>
	);
};

const isActiveRow = (row: ScoringInstructionRow, score: number | null): boolean => {
	if (score === null) {
		return false;
	}
	return score >= row.minScore && score <= row.maxScore;
};

const getAdjacentIndices = (rows: ScoringInstructionRow[], score: number | null): Set<number> => {
	if (score === null) {
		return new Set();
	}
	const activeIdx = rows.findIndex((r) => score >= r.minScore && score <= r.maxScore);
	if (activeIdx === -1) {
		return new Set();
	}
	const indices = new Set<number>();
	indices.add(activeIdx);
	if (activeIdx > 0) {
		indices.add(activeIdx - 1);
	}
	if (activeIdx < rows.length - 1) {
		indices.add(activeIdx + 1);
	}
	return indices;
};

export const ScoringInstructionsPanel: React.FC<ScoringInstructionsPanelProps> = ({
	instructions,
	isLoading = false,
	activeScore,
	isOpen,
	onToggle,
	anchorElement,
	containerElement,
}) => {
	const panelRef = useRef<HTMLDivElement>(null);
	const [dragOffset, setDragOffset] = useState<{ x: number; y: number } | null>(null);
	const [userPosition, setUserPosition] = useState<{ left: number; top: number; } | null>(null);
	const isDragging = useRef(false);
	const dragStart = useRef<{ mouseX: number; mouseY: number; panelX: number; panelY: number }>({
		mouseX: 0,
		mouseY: 0,
		panelX: 0,
		panelY: 0,
	});

	// Reset user position when anchor changes (user focused a new row)
	useEffect(() => {
		setUserPosition(null);
	}, [anchorElement]);

	// Calculate fixed position relative to viewport, recalculating on scroll
	const [autoPosition, setAutoPosition] = useState<{ left: number; top: number; } | null>(null);

	const recalcPosition = useCallback(() => {
		if (!anchorElement || !containerElement) {
			setAutoPosition(null);
			return;
		}
		const anchorRect = anchorElement.getBoundingClientRect();
		const containerRect = containerElement.getBoundingClientRect();
		const viewportHeight = window.innerHeight;

		const anchorBottom = anchorRect.bottom;
		const anchorTop = anchorRect.top;

		// Check if there's room below in viewport
		const spaceBelow = viewportHeight - anchorBottom - GAP;
		const placeBelow = spaceBelow >= PANEL_HEIGHT_ESTIMATE;

		const top = placeBelow
			? anchorBottom + GAP
			: Math.max(VIEWPORT_PADDING, anchorTop - PANEL_HEIGHT_ESTIMATE - GAP);

		// Right-align within the container's viewport position
		const left = containerRect.right - PANEL_MAX_WIDTH - VIEWPORT_PADDING;

		setAutoPosition({ top, left: Math.max(VIEWPORT_PADDING, left) });
	}, [anchorElement, containerElement]);

	useEffect(() => {
		recalcPosition();

		if (!containerElement) {
			return;
		}
		const handleScroll = () => {
			if (!userPosition) {
				recalcPosition();
			}
		};
		containerElement.addEventListener("scroll", handleScroll, { passive: true });
		window.addEventListener("resize", handleScroll, { passive: true });
		return () => {
			containerElement.removeEventListener("scroll", handleScroll);
			window.removeEventListener("resize", handleScroll);
		};
	}, [containerElement, recalcPosition, userPosition]);

	const currentPosition = userPosition ?? autoPosition;

	// Drag handlers
	const handleMouseDown = useCallback(
		(e: React.MouseEvent) => {
			if (!panelRef.current) {
				return;
			}
			isDragging.current = true;
			const rect = panelRef.current.getBoundingClientRect();
			dragStart.current = {
				mouseX: e.clientX,
				mouseY: e.clientY,
				panelX: currentPosition?.left ?? rect.left,
				panelY: currentPosition?.top ?? rect.top,
			};
			e.preventDefault();
		},
		[currentPosition]
	);

	useEffect(() => {
		const handleMouseMove = (e: MouseEvent) => {
			if (!isDragging.current) {
				return;
			}
			const dx = e.clientX - dragStart.current.mouseX;
			const dy = e.clientY - dragStart.current.mouseY;
			setUserPosition({
				top: dragStart.current.panelY + dy,
				left: dragStart.current.panelX + dx,
			});
		};

		const handleMouseUp = () => {
			isDragging.current = false;
		};

		document.addEventListener("mousemove", handleMouseMove);
		document.addEventListener("mouseup", handleMouseUp);
		return () => {
			document.removeEventListener("mousemove", handleMouseMove);
			document.removeEventListener("mouseup", handleMouseUp);
		};
	}, []);

	// Filter rows: if score entered, show active + one above + one below
	const visibleRows = useMemo(() => {
		if (!instructions) {
			return [];
		}
		if (activeScore === null) {
			return instructions.rows;
		}
		const indices = getAdjacentIndices(instructions.rows, activeScore);
		if (indices.size === 0) {
			return instructions.rows;
		}
		return instructions.rows.filter((_, idx) => indices.has(idx));
	}, [instructions, activeScore]);

	if (!isOpen) {
		return null;
	}

	// On mobile (no anchor / small screen), render inline at bottom
	const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

	if (isMobile) {
		return (
			<div
				className="fixed bottom-0 left-0 right-0 z-50 rounded-t-xl bg-white border-t border-gray-200 shadow-2xl"
				data-testid="scoring-instructions-panel"
			>
				<div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
					<div className="flex items-center gap-2 text-sm font-medium text-gray-700">
						<FiInfo size={14} className="text-blue-500" />
						Scoring Guide
					</div>
					<button
						type="button"
						onClick={onToggle}
						className="rounded-md p-1.5 text-gray-400 hover:bg-gray-200 hover:text-gray-600 transition-colors"
						aria-label="Hide scoring guide"
						data-testid="scoring-instructions-close"
					>
						<FiX size={16} />
					</button>
				</div>
				<div className="overflow-y-auto px-4 py-3 space-y-1.5" style={{ maxHeight: "35vh" }}>
					{renderContent(isLoading, instructions, visibleRows, activeScore)}
				</div>
			</div>
		);
	}

	// Desktop: floating panel (fixed to viewport so it stays visible on scroll)
	return (
		<div
			ref={panelRef}
			className="fixed z-50 rounded-lg bg-white border border-gray-200 shadow-lg"
			style={{
				width: `min(${PANEL_MAX_WIDTH}px, calc(100vw - 32px))`,
				...(currentPosition
					? { top: currentPosition.top, left: currentPosition.left }
					: { top: VIEWPORT_PADDING, right: VIEWPORT_PADDING }),
			}}
			data-testid="scoring-instructions-panel"
		>
			<div
				className="flex items-center justify-between rounded-t-lg bg-gray-50 px-3 py-2 border-b border-gray-200 cursor-grab active:cursor-grabbing select-none"
				onMouseDown={handleMouseDown}
				data-testid="scoring-instructions-handle"
			>
				<div className="flex items-center gap-2 text-xs font-medium text-gray-700">
					<FiMove size={12} className="text-gray-400" />
					Scoring Guide
				</div>
				<button
					type="button"
					onClick={onToggle}
					className="rounded-md p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-600 transition-colors"
					aria-label="Hide scoring guide"
					data-testid="scoring-instructions-close"
				>
					<FiX size={14} />
				</button>
			</div>
			<div className="overflow-y-auto p-3 space-y-1.5" style={{ maxHeight: "50vh" }}>
				{renderContent(isLoading, instructions, visibleRows, activeScore)}
			</div>
		</div>
	);
};

function renderContent(
	isLoading: boolean,
	instructions: ScoringInstruction | null,
	visibleRows: ScoringInstructionRow[],
	activeScore: number | null
): React.ReactNode {
	if (isLoading) {
		return (
			<p className="text-sm text-gray-400 text-center py-3" data-testid="scoring-instructions-loading">
				Loading...
			</p>
		);
	}

	if (!instructions) {
		return (
			<p className="text-sm text-gray-400 text-center py-3" data-testid="scoring-instructions-empty">
				No scoring instructions available.
			</p>
		);
	}

	return (
		<>
			<div className="mb-2">
				<p className="text-xs text-gray-500">
					Total Score: <span className="font-medium text-gray-700">{formatScore(instructions.totalScore)}</span>
				</p>
			</div>
			{visibleRows.map((row) => {
				const active = isActiveRow(row, activeScore);
				return (
					<div
						key={row.id}
						className={cn(
							"rounded-md border px-2.5 py-2 text-sm transition-colors",
							active
								? "border-blue-400 bg-blue-50 ring-1 ring-blue-200"
								: "border-gray-200 bg-white"
						)}
						data-testid={`instruction-row-${row.id}`}
						data-active={active}
					>
						<div className="flex items-start gap-2.5">
							<span
								className={cn(
									"inline-flex items-center justify-center rounded-md px-2.5 py-0.5 text-sm font-bold whitespace-nowrap tabular-nums shrink-0 mt-0.5",
									active
										? "bg-blue-200 text-blue-800"
										: "bg-gray-100 text-gray-700"
								)}
							>
								<RangeDisplay row={row} />
							</span>
							<div
								className={cn(
									"text-xs leading-relaxed [&_p]:m-0 [&_ul]:m-0 [&_ol]:m-0",
									active ? "text-blue-700" : "text-gray-600"
								)}
								dangerouslySetInnerHTML={{ __html: row.description }}
							/>
						</div>
					</div>
				);
			})}
		</>
	);
}
